import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { NavigationTrackingService } from '../services/navigation-tracking.service';

/**
 * Guard that prevents hospital users from accessing certain pages
 * Hospital users should only access /tasks and /profile
 * Decides on the /auth/me user: after a reload the stored one is the sign-in payload until /auth/me answers.
 */
export const hospitalAccessGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const navigationTrackingService = inject(NavigationTrackingService);
  const check = () => decide(authService, router, navigationTrackingService, state.url);
  const pending = authService.pendingUserRefresh();
  return pending ? pending.then(check, check) : check();
};

function decide(authService: AuthService, router: Router, navigationTrackingService: NavigationTrackingService, url: string): boolean {
  // First check if user is authenticated
  if (!authService.isAuthenticated()) {
    router.navigate(['/']);
    return false;
  }

  // Get current user
  const user = authService.user();
  if (!user) {
    router.navigate(['/']);
    return false;
  }

  // Check if user is hospital user
  const profileRole = user.profile?.role?.toLowerCase();
  
  if (profileRole === 'hospital') {
    // Hospital users can access /tasks, /profile, /patients (list), /tracking, /drug-reactions/{id}, and /patients/{id} (when coming from task detail)
    const allowedPaths = ['/tasks', '/profile', '/patients', '/tracking', '/drug-reactions', '/chronic-medications'];
    const currentPath = url.split('?')[0]; // Remove query params
    const isTaskDetail = currentPath.startsWith('/tasks/');
    const isPatientDetail = currentPath.startsWith('/patients/') && currentPath !== '/patients';
    const isDrugReactionDetail = currentPath.startsWith('/drug-reactions/');
    const isChronicMedicationDetail = currentPath.startsWith('/chronic-medications/');
    // Check navigation context for router state
    const routerState = (router as any).getCurrentNavigation()?.extras?.state;
    if (routerState) {
      navigationTrackingService.updateFromRouterState(routerState);
    }
    
    if (allowedPaths.includes(currentPath) || isTaskDetail || isDrugReactionDetail || isChronicMedicationDetail) {
      return true; // Allow access to tasks, profile, patients list, and drug reaction details
    } else if (isPatientDetail) {
      // For patient detail pages, check if navigation came from task detail or patients list
      const hasValidAccess = navigationTrackingService.hasValidHospitalAccess();
      if (hasValidAccess) {
        return true; // Allow access when coming from task detail
      } else {
        // Redirect to patients list if trying to access patient detail directly
        router.navigate(['/patients']);
        return false;
      }
    } else {
      // Redirect to tasks page for hospital users trying to access other forbidden pages
      router.navigate(['/tasks']);
      return false;
    }
  }

  // Non-hospital users can access this page
  return true;
}
